<template>
    <div class="promotion-summary td-container">

        <promotion :promotion="promotion" :listing="listing" :show-edit="enableEdit" @edit-promotion="toggleEditPromotionModal"></promotion>

        <div v-if="showEditPromotion">
            <edit-promotion @cancel="cancelEdit" @promotion-update="updatePromo" :promotion="promotion"></edit-promotion>
        </div>

        <div class="td-card grey rounded">
            <div class="col-3 mx-0 px-0">
                <div style="margin: 0; padding: 0;">
                    <strong>Site:</strong> {{ siteName }}
                </div>
                <div style="margin: 0; padding: 0;">
                    <strong>Status:</strong> {{ status }}
                </div>
                <div style="margin: 0; padding: 0;">
                    <strong>Expiry:</strong> {{ expiryDate }}
                </div>
            </div>
            <div class="col-3 mt-2">
                <div>
                    <strong>Balance:</strong> {{ currentBalance }}<br />
                    <span v-if="promotion.funds.dailyLimit > 0"><strong>Daily Remaining:</strong> {{ todayBalance }}<br /></span>
                    <strong>Daily Budget: </strong> {{ dailyBudgetDisplay }}<br />
                </div>
                <div v-if="promotion.funds.recurringCharge && promotion.funds.recurringCharge.toBalance > 0">
                    <strong>Next Recurring Charge:</strong> {{ nextCharge }}<br />
                    <strong>Up To:</strong> {{ promotion.funds.recurringCharge?.toBalance == null ? "Not Set" : new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(promotion.funds.recurringCharge.toBalance) }} <br />
                </div>
                <div v-if="isCsrOrAdmin">
                    <strong>Total Funds to Date:</strong> {{ total }} <br />
                    <strong>Pending Charges:</strong> {{ reserved }}
                </div>
            </div>
            <div class="mt-2">
                <div>
                    <div v-if="showRecurringCharge">
                        <recurring-charge :taxname="taxname" :taxrate="taxrate" :promotion="promotion" @cancel="toggleRecurringChargeModal" @update="updatePromo"></recurring-charge>
                    </div>
                    <div v-if="showRemovePromotion">
                        <remove-promotion :promotionid="promotion.id" @update="removePromo" @cancel="toggleRemovePromotionModal"></remove-promotion>
                    </div>
                    <div v-if="showDailyLimit">
                        <daily-limit :promotion="promotion" @cancel="toggleDailyLimitModal" @update="updatePromo"></daily-limit>
                    </div>
                    <div v-if="showRemoveFunds">
                        <remove-funds :promotion="promotion" @cancel="toggleRemoveFundsModal" @update="updatePromo"></remove-funds>
                    </div>
                    <div v-if="showAddFunds">
                        <add-funds :taxname="taxname" :taxrate="taxrate" :promotion="promotion" @cancel="toggleAddFundsModal" @update="updatePromo"></add-funds>
                    </div>

                    <div>
                        <div class="td-banner warning border" v-if="promotion.status == PromotionStatus.PendingApproval">
                            <icon type="solid" name="faTriangleExclamation"></icon>
                            <span>This is a new promotion is not currently displaying in search results.  Please allow 1-2 business days for review.</span>
                        </div>
                    </div>

                    <div v-if="promotion.edit && promotion.edit != undefined">
                        <div class="td-banner warning border">
                            <icon typeof="solid" name="faTriangleExclamation"></icon>
                            <span>The following changes are currently under review.  Please allow 1-2 business days for review.</span>
                        </div>

                        <div class="td-banner border">
                            <p class="mb-1"><strong>Title:</strong> {{ promotion.edit.title }}</p>
                            <p><strong>Message:</strong> {{ promotion.edit.message }}</p>
                        </div>
                    </div>


                    <div v-if="enableEdit" class="action-links mt-3">
                        <a v-if="promotion.status != PromotionStatus.Inactive"
                           class="td-link primary no-underline px-1" @click.prevent="toggleDailyLimitModal">
                            <icon type="solid" name="faMoneyBill"></icon> Manage Daily Limit
                        </a>

                        <a v-if="promotion.status == PromotionStatus.Active && promotion.funds.recurringCharge.toBalance == 0.00"
                           class="td-link primary no-underline px-1" @click.prevent="toggleRecurringChargeModal">
                            <icon type="solid" name="faCalendarDays"></icon> Set-up Recurring Charge
                        </a>

                        <a v-if="promotion.status == PromotionStatus.Active && promotion.funds.recurringCharge.toBalance != 0.00"
                           class="td-link primary no-underline px-1" @click.prevent="toggleRecurringChargeModal">
                            <icon type="solid" name="faCalendarDays"></icon> Edit Recurring Charge
                        </a>

                        <a v-if="promotion.status == PromotionStatus.Active"
                           class="td-link primary no-underline px-1" @click.prevent="toggleAddFundsModal">
                            <icon type="solid" name="faPlus"></icon> Add Funds
                        </a>

                        <a v-if="currentAmount > 0.00"
                           class="td-link primary no-underline px-1" @click.prevent="toggleRemoveFundsModal">
                            <icon type="solid" name="faMoneyBillTransfer"></icon> Remove Funds
                        </a>

                        <template v-if="isCsrOrAdmin">

                            <a v-if="promotion.status == PromotionStatus.PendingApproval" class="td-link primary no-underline px-1" @click.prevent="approvePromo">
                                <icon type="solid" name="faCheck"></icon> Activate Promo
                            </a>
                            <a v-if="showPendingEditOptions" class="td-link primary no-underline px-1" @click.prevent="approveEditClick">
                                <icon type="solid" name="faCheck"></icon> Approve Edit
                            </a>
                            <a v-if="showPendingEditOptions" class="td-link primary no-underline px-1" @click.prevent="removeEditClick">
                                <icon type="solid" name="faCheck"></icon> Remove Edit
                            </a>

                        </template>

                        <a class="td-link primary no-underline px-1" @click.prevent="toggleRemovePromotionModal">
                            <icon type="solid" name="faXmark"></icon> Remove
                        </a>
                    </div>

                    <div class="mt-3">
                        <div v-if="promotion.applyToAllLocations">
                            <div class="td-card shadow rounded">
                                All Locations
                            </div>
                        </div>
                        <div v-if="promotion.locationIds.length > 0 && !promotion.applyToAllLocations">
                            <div class="td-card shadow rounded" @click="showPromotionLocations = !showPromotionLocations" v-if="showPromotionLocations == false"> (+) Specific Locations</div>
                            <div class="td-card shadow rounded mb-3" v-if="showPromotionLocations == true" @click="showPromotionLocations = !showPromotionLocations"> (-) Hide</div>
                            <div class="md-col-2" v-if="showPromotionLocations == true">
                                <div v-for="locationId in promotion.locationIds" class="td-card shadow rounded">
                                    <p>{{ locationDisplay(locationId) }}</p>
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import script from './PromotionSummary';
    export default script;</script>

<style lang="less">
    @import "./PromotionSummary.less";
</style>
